/**
 * App shell: adult sign-in (when Supabase is configured) → student picker → lesson.
 * Without Supabase configured, it runs on LocalStore so the pilot can be tried on any device immediately.
 */
import { LocalStore, type Store, type Student } from "../data/store";
import { SupabaseStore, supabaseConfigured, auth, session } from "../data/supabase";
import { mountHome } from "./home";
import { mountTeacher } from "./teacher";
import { esc } from "./keypad";
import { mountPlans, planLine } from "./plans";

export async function startApp(root: HTMLElement): Promise<void> {
  if (!supabaseConfigured()) return showStudents(root, new LocalStore(), null);
  const s = await session();
  if (s) return showStudents(root, new SupabaseStore(s.user.id), s.user.email ?? null);
  showSignIn(root);
  auth.onChange((sess) => {
    if (sess) showStudents(root, new SupabaseStore(sess.user.id), sess.user.email ?? null);
    else showSignIn(root);
  });
}

function shell(inner: string): string {
  return `<header class="band"><div class="wrap"><div><div class="brand">THE TIGARD METHOD<sup>™</sup></div><div class="tagline">Pattern · Logic · Discovery</div></div></div></header>
  <div class="shell">${inner}</div>`;
}

function showSignIn(root: HTMLElement) {
  root.innerHTML = shell(`<h1>Tigard Math</h1>
    <p class="muted">A parent or teacher signs in. Students are added inside the account by first name only.</p>
    <form id="f" class="card">
      <div class="field"><label for="email">Email</label><input id="email" type="email" autocomplete="email" required></div>
      <div class="field"><label for="pw">Password</label><input id="pw" type="password" autocomplete="current-password" minlength="8" required></div>
      <div class="field" id="nameField" hidden><label for="dn">Your name</label><input id="dn" autocomplete="name"></div>
      <div class="row"><button class="btn" type="submit" id="go">Sign in</button><button class="btn ghost" type="button" id="toggle">Create an account</button></div>
      <p class="fb warn" id="err"></p>
    </form>`);
  let creating = false;
  const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;
  $("toggle").onclick = () => {
    creating = !creating;
    $("nameField").hidden = !creating;
    $("go").textContent = creating ? "Create account" : "Sign in";
    $("toggle").textContent = creating ? "I have an account" : "Create an account";
  };
  $<HTMLFormElement>("f").onsubmit = async (e) => {
    e.preventDefault();
    const email = $<HTMLInputElement>("email").value.trim(), pw = $<HTMLInputElement>("pw").value;
    const r = creating ? await auth.signUp(email, pw, $<HTMLInputElement>("dn").value.trim()) : await auth.signIn(email, pw);
    if (r.error) $("err").textContent = r.error.message;
    else if (creating && !r.data.session) $("err").textContent = "Check your email to confirm the account, then sign in.";
  };
}

async function showStudents(root: HTMLElement, store: Store, email: string | null) {
  const students = await store.listStudents();
  root.innerHTML = shell(`<h1>Who's practicing?</h1>
    ${email ? `<p class="who" style="color:var(--muted)"><span>${esc(email)}</span><button type="button" id="out">Sign out</button></p>` : `<p class="muted">No account yet: progress is saved in this browser only.</p>`}
    ${paidNote()}
    ${store.kind === "supabase" ? `<div class="row" style="margin:0 0 12px"><span class="floor" id="plan" style="margin:0"></span><button class="btn ghost small" type="button" id="plans">Plans</button></div>` : ""}
    <div class="students" id="list"></div>
    <form id="add" class="card">
      <div class="field"><label for="fn">Add a student (first name or nickname)</label><input id="fn" maxlength="40" required></div>
      <div class="field"><label for="gr">Grade</label><input id="gr" type="number" min="0" max="12" value="3"></div>
      <div class="row"><button class="btn" type="submit">Add</button><label class="toggle" style="margin:0"><input type="checkbox" id="preview"> Teacher preview</label></div>
    </form>
    <div class="row" style="margin-top:18px"><button class="btn ghost" type="button" id="teacher">Teacher view</button><span class="note" style="margin:0">Roster, fact maps, recent answers.</span></div>`);
  const $ = <T extends HTMLElement>(id: string) => root.querySelector<T>(`#${id}`)!;
  const list = $("list");
  const open = async (s: Student) => {
    const progress = await store.loadProgress(s.id);
    mountHome(root, { store, student: s, progress, preview: $<HTMLInputElement>("preview")?.checked ?? false, onBack: () => showStudents(root, store, email) });
    window.scrollTo({ top: 0 });
  };
  for (const s of students) {
    const b = document.createElement("button");
    b.type = "button"; b.className = "studentcard";
    b.innerHTML = `<b>${esc(s.firstName)}</b><span>${s.grade != null ? `Grade ${s.grade}` : ""}</span>`;
    b.onclick = () => open(s);
    list.appendChild(b);
  }
  if (!students.length) list.innerHTML = `<p class="muted">Add the first student below.</p>`;
  $<HTMLFormElement>("add").onsubmit = async (e) => {
    e.preventDefault();
    const fn = $<HTMLInputElement>("fn").value.trim(), gr = $<HTMLInputElement>("gr").value;
    if (!fn) return;
    try { const s = await store.addStudent(fn, gr === "" ? null : Number(gr)); open(s); }
    catch (err) { const n = root.querySelector("#addnote") ?? Object.assign(document.createElement("p"), { id: "addnote", className: "fb warn" }); n.textContent = (err as Error).message.replace(/^.*?: /, ""); $("add").appendChild(n); }
  };
  const pl = root.querySelector<HTMLButtonElement>("#plans");
  if (pl) {
    pl.onclick = () => mountPlans(root, store, () => showStudents(root, store, email));
    void store.myPlan().then((p) => { const el = root.querySelector("#plan"); if (el) el.innerHTML = planLine(p); });
  }
  const out = root.querySelector<HTMLButtonElement>("#out");
  if (out) out.onclick = () => auth.signOut();
  $("teacher").onclick = () => mountTeacher(root, { store, email, onBack: () => showStudents(root, store, email), onOpenStudent: open });
}

/** Back from Stripe Checkout (?paid=1 or ?paid=0): say so once, then tidy the address bar. */
function paidNote(): string {
  const q = new URLSearchParams(location.search).get("paid");
  if (q == null) return "";
  history.replaceState(null, "", location.pathname);
  return q === "1" ? `<p class="floor">Thank you! Your plan is active. (If it doesn't show yet, give it a minute and reopen this page.)</p>` : `<p class="note">No payment was made.</p>`;
}
